/**
 * The app shell.
 *
 * Navigation sits in the header on every size. On a desktop the destinations
 * are laid out across it; on a phone they collapse behind a hamburger that
 * drops a panel down from the header.
 *
 * A bottom bar used to hold them on a phone. It cost a permanent strip of the
 * screen and a matching strip of padding under every page, which is a lot to
 * pay for a handful of links on an app you mostly scroll.
 */

import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '@/auth/AuthContext'

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/consulting', label: 'Consulting', end: false },
  { to: '/notes', label: 'Notes', end: false },
  { to: '/archive', label: 'Archive', end: false },
  { to: '/settings', label: 'Settings', end: false },
]

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
    isActive ? 'bg-navy-soft text-navy' : 'text-ink-2 hover:bg-navy-softer hover:text-navy',
  ].join(' ')
}

export function Layout() {
  const { user, logout } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const toggleRef = useRef<HTMLButtonElement>(null)

  // Navigating is the whole purpose of the menu, so it closes itself on
  // arrival. Keyed on the location object rather than the path: tapping the
  // link for the page you are already on must still close it.
  useEffect(() => {
    setMenuOpen(false)
  }, [location])

  // Escape closes and returns focus to the control that opened it, so a
  // keyboard is not left stranded at the top of the document.
  useEffect(() => {
    if (!menuOpen) return
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      setMenuOpen(false)
      toggleRef.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [menuOpen])

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-30 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-4">
          <NavLink to="/" className="flex items-center gap-2">
            <span
              aria-hidden
              className="flex size-7 items-center justify-center rounded-md bg-navy text-sm font-bold text-white"
            >
              &#10003;
            </span>
            <span className="text-lg font-bold tracking-tight text-navy">Docket</span>
          </NavLink>

          <nav aria-label="Main" className="ml-4 hidden gap-1 md:flex">
            {NAV.map((entry) => (
              <NavLink key={entry.to} to={entry.to} end={entry.end} className={navClass}>
                {entry.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {user && <span className="hidden text-sm text-ink-3 sm:inline">{user.username}</span>}
            <button type="button" className="btn btn-neutral btn-sm" onClick={() => void logout()}>
              Sign out
            </button>

            {/* Three bars drawn rather than an icon font: the shell must not
                wait on a webfont to become usable. */}
            <button
              ref={toggleRef}
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className="flex size-9 shrink-0 flex-col items-center justify-center gap-[5px] rounded-md border border-line-strong transition-colors hover:bg-navy-softer md:hidden"
            >
              <span
                aria-hidden
                className={`block h-0.5 w-4 rounded-full bg-ink-2 transition-transform ${
                  menuOpen ? 'translate-y-[7px] rotate-45' : ''
                }`}
              />
              <span
                aria-hidden
                className={`block h-0.5 w-4 rounded-full bg-ink-2 transition-opacity ${
                  menuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                aria-hidden
                className={`block h-0.5 w-4 rounded-full bg-ink-2 transition-transform ${
                  menuOpen ? '-translate-y-[7px] -rotate-45' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav
            id="mobile-nav"
            aria-label="Main"
            className="border-t border-line bg-surface px-4 py-2 shadow-md md:hidden"
          >
            <div className="mx-auto flex max-w-5xl flex-col">
              {NAV.map((entry) => (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.end}
                  className={({ isActive }) =>
                    [
                      // Full-width rows with a generous target: this is the
                      // one part of the app used exclusively by thumb.
                      'rounded-md px-3 py-3 text-base font-medium transition-colors',
                      isActive ? 'bg-navy-soft text-navy' : 'text-ink-2',
                    ].join(' ')
                  }
                >
                  {entry.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      {/* Tapping the page closes the menu. A transparent sheet under the panel
          does that without every page having to know the menu exists. */}
      {menuOpen && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-20 cursor-default md:hidden"
        />
      )}

      <main className="mx-auto max-w-5xl px-4 py-4 pb-8">
        <Outlet />
      </main>
    </div>
  )
}
